'use client'

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '@/core/utils/api-client'
import { useActiveTimer } from './use-active-timer'
import type { Issue } from '@/shared/schema/types'
import type { GiteaCommit } from '@/core/services/gitea'

// ---------------------------------------------------------------------------
// Cross-window sync via Tauri events
// ---------------------------------------------------------------------------

/** Notify other Tauri windows that timer state changed */
async function emitTimerChanged() {
  if (!('__TAURI_INTERNALS__' in window)) return
  try {
    const { emit } = await import('@tauri-apps/api/event')
    await emit('timer-changed', {})
  } catch { /* not in Tauri */ }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Segment {
  start: number // ms
  end: number   // ms
}

export type StopPhase = 'saving' | 'checking-commits' | 'finalizing' | 'error'

export interface TimerServiceState {
  // Core timer
  isRunning: boolean
  isPaused: boolean
  startTime: number | null
  currentSegmentStart: number | null
  accumulatedSeconds: number
  elapsed: number              // ticking display value
  segments: Segment[]

  // Metadata
  selectedIssue: Issue | null
  selectedWorkType: string | null
  description: string

  // Stop flow
  stopPhase: StopPhase | 'idle'

  // Commit association (post-stop)
  pendingCommits: GiteaCommit[]
  pendingEntryId: string | null
  selectedCommitShas: Set<string>
}

export interface TimerService {
  state: TimerServiceState
  isActive: boolean
  isStopping: boolean
  hasCommits: boolean

  // Actions
  start: () => void
  pause: () => void
  resume: () => void
  stop: () => void
  discard: () => void

  // Metadata setters
  setIssue: (issue: Issue | null) => void
  setWorkType: (id: string | null) => void
  setDescription: (desc: string) => void

  // Commit association
  toggleCommit: (sha: string) => void
  associateCommits: () => void
  skipCommits: () => void

  // Loading state
  initialized: boolean
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

const EMPTY: TimerServiceState = {
  isRunning: false,
  isPaused: false,
  startTime: null,
  currentSegmentStart: null,
  accumulatedSeconds: 0,
  elapsed: 0,
  segments: [],
  selectedIssue: null,
  selectedWorkType: null,
  description: '',
  stopPhase: 'idle',
  pendingCommits: [],
  pendingEntryId: null,
  selectedCommitShas: new Set(),
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTimerService(): TimerService {
  const queryClient = useQueryClient()
  const [state, setState] = useState<TimerServiceState>(EMPTY)
  const [initialized, setInitialized] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const prevSyncRef = useRef<string>('')

  // Server state via React Query (used for external sync)
  const { data: serverSession } = useActiveTimer()

  // -------------------------------------------------------------------
  // Load session from server on mount
  // -------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const res = await apiFetch('/api/timetracking/sync')
        if (!res.ok || cancelled) { setInitialized(true); return }
        const session = await res.json()
        if (!session || cancelled) { setInitialized(true); return }

        const startTime = new Date(session.startTime).getTime()
        const accumulatedSeconds: number = session.accumulatedSeconds || 0
        const segments: Segment[] = (session.segments || []).map((s: { start: string; end: string }) => ({
          start: new Date(s.start).getTime(),
          end: new Date(s.end).getTime(),
        }))
        const isPaused = !!session.pausedAt
        const currentSegmentStart = session.currentSegmentStart
          ? new Date(session.currentSegmentStart).getTime()
          : null

        let elapsed: number
        if (isPaused) {
          elapsed = accumulatedSeconds
        } else if (currentSegmentStart) {
          elapsed = accumulatedSeconds + Math.floor((Date.now() - currentSegmentStart) / 1000)
        } else {
          elapsed = accumulatedSeconds
        }

        if (!cancelled) {
          setState(prev => ({
            ...prev,
            isRunning: !isPaused,
            isPaused,
            startTime,
            currentSegmentStart,
            accumulatedSeconds,
            elapsed,
            segments,
            description: session.description || '',
            selectedWorkType: session.youtrackWorkTypeId || null,
            // Issue data is not joined in sync GET — needs re-selection on refresh
            selectedIssue: null,
          }))
        }
      } catch { /* ignore load errors */ }
      if (!cancelled) setInitialized(true)
    }
    load()
    return () => { cancelled = true }
  }, [])

  // -------------------------------------------------------------------
  // Listen for cross-window timer-changed events (Tauri only)
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!('__TAURI_INTERNALS__' in window)) return
    let unlisten: (() => void) | undefined
    import('@tauri-apps/api/event').then(({ listen }) => {
      listen('timer-changed', () => {
        queryClient.invalidateQueries({ queryKey: ['/api/timetracking/sync'] })
      }).then(fn => { unlisten = fn })
    })
    return () => { unlisten?.() }
  }, [queryClient])

  // -------------------------------------------------------------------
  // Elapsed tick — single interval for the whole app
  // -------------------------------------------------------------------
  useEffect(() => {
    if (state.isRunning && state.currentSegmentStart) {
      intervalRef.current = setInterval(() => {
        setState(prev => ({
          ...prev,
          elapsed: prev.accumulatedSeconds +
            Math.floor((Date.now() - (prev.currentSegmentStart ?? Date.now())) / 1000),
        }))
      }, 1000)
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [state.isRunning, state.currentSegmentStart])

  // -------------------------------------------------------------------
  // Sync external changes (tray, overlay, other devices)
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!initialized) return

    // Deleted externally
    if (serverSession === null && (state.isRunning || state.isPaused)) {
      setState(EMPTY)
      return
    }

    // Changed externally — sync all fields
    if (serverSession) {
      const externallyPaused = !!serverSession.pausedAt
      const accumulatedSeconds = serverSession.accumulatedSeconds || 0
      const currentSegmentStart = serverSession.currentSegmentStart
        ? new Date(serverSession.currentSegmentStart).getTime()
        : null
      const startTime = new Date(serverSession.startTime).getTime()

      setState(prev => {
        // If issue changed, fetch the full Issue object
        const issueChanged = serverSession.issueId !== (prev.selectedIssue?.id ?? null)
        if (issueChanged && serverSession.issueId) {
          // Fire-and-forget issue lookup
          apiFetch(`/api/projects/issues/search?q=${encodeURIComponent(serverSession.issueId)}&top=1`)
            .then(res => res.ok ? res.json() : [])
            .then((issues: Issue[]) => {
              if (issues.length > 0) {
                setState(s => ({ ...s, selectedIssue: issues[0] }))
              }
            })
            .catch(() => { /* ignore */ })
        }

        return {
          ...prev,
          isRunning: !externallyPaused,
          isPaused: externallyPaused,
          startTime,
          accumulatedSeconds,
          currentSegmentStart,
          elapsed: externallyPaused
            ? accumulatedSeconds
            : currentSegmentStart
              ? accumulatedSeconds + Math.floor((Date.now() - currentSegmentStart) / 1000)
              : accumulatedSeconds,
          description: serverSession.description ?? prev.description,
          selectedWorkType: serverSession.youtrackWorkTypeId ?? prev.selectedWorkType,
          // Clear issue if it changed (will be populated by async fetch above)
          selectedIssue: issueChanged ? null : prev.selectedIssue,
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync on serverSession changes
  }, [serverSession, initialized])

  // -------------------------------------------------------------------
  // Persist to server helper
  // -------------------------------------------------------------------
  const persist = useCallback(async (s: TimerServiceState) => {
    if (!s.startTime) return
    await apiFetch('/api/timetracking/sync', {
      method: 'POST',
      body: JSON.stringify({
        startTime: new Date(s.startTime).toISOString(),
        description: s.description || null,
        issueId: s.selectedIssue?.id || null,
        projectId: s.selectedIssue?.project?.id || null,
        youtrackWorkTypeId: s.selectedWorkType || null,
        pausedAt: s.isPaused ? new Date().toISOString() : null,
        accumulatedSeconds: s.accumulatedSeconds,
        currentSegmentStart: s.currentSegmentStart
          ? new Date(s.currentSegmentStart).toISOString()
          : null,
        segments: s.segments.map(seg => ({
          start: new Date(seg.start).toISOString(),
          end: new Date(seg.end).toISOString(),
        })),
      }),
    })
    queryClient.invalidateQueries({ queryKey: ['/api/timetracking/sync'] })
    emitTimerChanged()
  }, [queryClient])

  // Auto-persist metadata changes
  useEffect(() => {
    if (!initialized || (!state.isRunning && !state.isPaused)) return
    const syncKey = JSON.stringify({
      d: state.description,
      i: state.selectedIssue?.id,
      w: state.selectedWorkType,
    })
    if (syncKey !== prevSyncRef.current) {
      prevSyncRef.current = syncKey
      persist(state)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, state.isRunning, state.isPaused, state.description, state.selectedIssue, state.selectedWorkType])

  // -------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------

  const start = useCallback(() => {
    if (!state.selectedIssue) return
    const now = Date.now()
    const newState: TimerServiceState = {
      ...state,
      isRunning: true,
      isPaused: false,
      startTime: now,
      currentSegmentStart: now,
      accumulatedSeconds: 0,
      elapsed: 0,
      segments: [],
      stopPhase: 'idle',
      pendingCommits: [],
      pendingEntryId: null,
      selectedCommitShas: new Set(),
    }
    setState(newState)
    persist(newState)
  }, [state, persist])

  const pause = useCallback(() => {
    const now = Date.now()
    const segDuration = state.currentSegmentStart
      ? Math.floor((now - state.currentSegmentStart) / 1000)
      : 0
    const newAccumulated = state.accumulatedSeconds + segDuration
    const newSegments = state.currentSegmentStart
      ? [...state.segments, { start: state.currentSegmentStart, end: now }]
      : state.segments

    const newState: TimerServiceState = {
      ...state,
      isRunning: false,
      isPaused: true,
      currentSegmentStart: null,
      accumulatedSeconds: newAccumulated,
      elapsed: newAccumulated,
      segments: newSegments,
    }
    setState(newState)
    persist(newState)
  }, [state, persist])

  const resume = useCallback(() => {
    const now = Date.now()
    const newState: TimerServiceState = {
      ...state,
      isRunning: true,
      isPaused: false,
      currentSegmentStart: now,
    }
    setState(newState)
    persist(newState)
  }, [state, persist])

  const discard = useCallback(async () => {
    await apiFetch('/api/timetracking/sync', { method: 'DELETE' })
    setState(EMPTY)
    queryClient.invalidateQueries({ queryKey: ['/api/timetracking/sync'] })
    emitTimerChanged()
  }, [queryClient])

  const stop = useCallback(async () => {
    if (!state.isRunning && !state.isPaused) return

    const now = Date.now()
    const finalSegments = [...state.segments]
    let totalSeconds = state.accumulatedSeconds

    if (state.isRunning && state.currentSegmentStart) {
      totalSeconds += Math.floor((now - state.currentSegmentStart) / 1000)
      finalSegments.push({ start: state.currentSegmentStart, end: now })
    }

    // Under 1 minute — auto-discard
    if (totalSeconds < 60) {
      await discard()
      return
    }

    if (!state.startTime) return
    const startTime = new Date(state.startTime)
    const endTime = new Date(now)
    const localDate = `${startTime.getFullYear()}-${String(startTime.getMonth() + 1).padStart(2, '0')}-${String(startTime.getDate()).padStart(2, '0')}`

    const preStopState = { ...state }

    // Phase 1: Saving
    setState(prev => ({ ...prev, stopPhase: 'saving' }))

    let entry: { id: string } | undefined
    try {
      const res = await apiFetch('/api/timetracking/entries', {
        method: 'POST',
        body: JSON.stringify({
          startTime,
          endTime,
          duration: totalSeconds,
          localDate,
          description: state.description || undefined,
          issueId: state.selectedIssue?.id,
          projectId: state.selectedIssue?.project?.id,
          youtrackWorkTypeId: state.selectedWorkType ?? undefined,
          segments: finalSegments.map(s => ({
            start: new Date(s.start).toISOString(),
            end: new Date(s.end).toISOString(),
          })),
        }),
      })
      if (!res.ok) throw new Error('Failed to create time entry')
      entry = await res.json()
    } catch {
      setState(prev => ({ ...prev, stopPhase: 'error' }))
      await new Promise(resolve => setTimeout(resolve, 2000))
      setState({ ...preStopState, stopPhase: 'idle' })
      return
    }

    queryClient.invalidateQueries({ queryKey: ['/api/timetracking/entries'] })
    queryClient.invalidateQueries({ queryKey: ['/api/timetracking/reports'] })

    // Phase 2: Check commits
    setState(prev => ({ ...prev, stopPhase: 'checking-commits' }))

    await apiFetch('/api/timetracking/sync', { method: 'DELETE' })
    queryClient.invalidateQueries({ queryKey: ['/api/timetracking/sync'] })
    emitTimerChanged()

    let foundCommits: GiteaCommit[] = []
    if (entry?.id) {
      try {
        const res = await apiFetch(
          `/api/timetracking/commits?from=${startTime.toISOString()}&to=${endTime.toISOString()}`
        )
        if (res.ok) foundCommits = await res.json()
      } catch { /* non-critical */ }
    }

    // Phase 3: Finalize
    setState(prev => ({ ...prev, stopPhase: 'finalizing' }))
    await new Promise(resolve => setTimeout(resolve, 400))

    // Reset, but keep commits if found
    if (foundCommits.length > 0 && entry?.id) {
      setState({
        ...EMPTY,
        pendingCommits: foundCommits,
        pendingEntryId: entry.id,
        selectedCommitShas: new Set(foundCommits.map(c => c.sha)),
      })
    } else {
      setState(EMPTY)
    }
  }, [state, discard, queryClient])

  // -------------------------------------------------------------------
  // Metadata setters
  // -------------------------------------------------------------------

  const setIssue = useCallback((issue: Issue | null) => {
    setState(prev => ({ ...prev, selectedIssue: issue, selectedWorkType: null }))
  }, [])

  const setWorkType = useCallback((id: string | null) => {
    setState(prev => ({ ...prev, selectedWorkType: id }))
  }, [])

  const setDescription = useCallback((desc: string) => {
    setState(prev => ({ ...prev, description: desc }))
  }, [])

  // -------------------------------------------------------------------
  // Commit association
  // -------------------------------------------------------------------

  const toggleCommit = useCallback((sha: string) => {
    setState(prev => {
      const next = new Set(prev.selectedCommitShas)
      if (next.has(sha)) next.delete(sha)
      else next.add(sha)
      return { ...prev, selectedCommitShas: next }
    })
  }, [])

  const associateCommits = useCallback(async () => {
    if (!state.pendingEntryId) return
    const selected = state.pendingCommits.filter(c => state.selectedCommitShas.has(c.sha))
    await apiFetch(`/api/timetracking/entries/${state.pendingEntryId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        commits: selected.map(c => ({ repo: c.repo, shortSha: c.shortSha })),
      }),
    })
    queryClient.invalidateQueries({ queryKey: ['/api/timetracking/entries'] })
    setState(prev => ({ ...prev, pendingCommits: [], pendingEntryId: null, selectedCommitShas: new Set() }))
  }, [state.pendingEntryId, state.pendingCommits, state.selectedCommitShas, queryClient])

  const skipCommits = useCallback(() => {
    setState(prev => ({ ...prev, pendingCommits: [], pendingEntryId: null, selectedCommitShas: new Set() }))
  }, [])

  // -------------------------------------------------------------------
  // Derived
  // -------------------------------------------------------------------

  const isActive = state.isRunning || state.isPaused
  const isStopping = state.stopPhase !== 'idle'
  const hasCommits = state.pendingCommits.length > 0 && !!state.pendingEntryId

  return useMemo(() => ({
    state,
    isActive,
    isStopping,
    hasCommits,
    start,
    pause,
    resume,
    stop,
    discard,
    setIssue,
    setWorkType,
    setDescription,
    toggleCommit,
    associateCommits,
    skipCommits,
    initialized,
  }), [state, isActive, isStopping, hasCommits, start, pause, resume, stop, discard, setIssue, setWorkType, setDescription, toggleCommit, associateCommits, skipCommits, initialized])
}
